import React from 'react';
import { Check } from 'lucide-react';

interface Step {
  number: number;
  label: string;
  completed: boolean;
}

interface Props {
  steps: Step[];
  currentStep: number;
  onStepClick: (step: number) => void;
  canNavigateToStep: (step: number) => boolean;
}

const WizardStepIndicator: React.FC<Props> = ({
  steps,
  currentStep,
  onStepClick,
  canNavigateToStep
}) => {
  // Progress is the current step's POSITION in the (possibly filtered) list, not its
  // number — step numbers are 0-based in the creation wizard and the list is filtered
  // in edit modes, so using the raw number produced negative and >100% widths.
  const currentIndex = steps.findIndex(s => s.number === currentStep);
  const progressPercent = steps.length <= 1 || currentIndex < 0
    ? 0
    : Math.min(100, Math.max(0, (currentIndex / (steps.length - 1)) * 100));

  return (
    <div className="w-full py-6">
      <div className="flex items-center justify-between max-w-4xl mx-auto">
        {steps.map((step, index) => {
          const isActive = step.number === currentStep;
          const isCompleted = step.completed;
          const isClickable = canNavigateToStep(step.number);
          const isLast = index === steps.length - 1;

          return (
            <React.Fragment key={step.number}>
              {/* Step Circle */}
              <div className="flex flex-col items-center relative">
                <button
                  onClick={() => isClickable && onStepClick(step.number)}
                  disabled={!isClickable}
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm transition-all ${
                    isActive
                      ? 'bg-primary-600 text-white ring-4 ring-primary-100 dark:ring-primary-900/30'
                      : isCompleted
                      ? 'bg-green-600 text-white hover:bg-green-700'
                      : isClickable
                      ? 'bg-background-tertiary dark:bg-background-dark-tertiary text-text-tertiary dark:text-text-dark-tertiary hover:bg-neutral-200 dark:hover:bg-background-dark-secondary'
                      : 'bg-background-tertiary dark:bg-background-dark-tertiary text-text-tertiary dark:text-text-dark-tertiary opacity-50 cursor-not-allowed'
                  } ${isClickable ? 'cursor-pointer' : ''}`}
                >
                  {isCompleted ? (
                    <Check className="w-5 h-5" />
                  ) : (
                    <span>{step.number}</span>
                  )}
                </button>
                <span
                  className={`mt-2 text-xs font-medium text-center whitespace-nowrap ${
                    isActive
                      ? 'text-primary-600 dark:text-primary-400'
                      : isCompleted
                      ? 'text-green-600 dark:text-green-400'
                      : 'text-text-tertiary dark:text-text-dark-tertiary'
                  }`}
                >
                  {step.label}
                </span>
              </div>

              {/* Connector Line */}
              {!isLast && (
                <div className="flex-1 h-0.5 mx-2 mt-[-24px]">
                  <div
                    className={`h-full transition-all ${
                      isCompleted
                        ? 'bg-green-600'
                        : isActive
                        ? 'bg-gradient-to-r from-primary-600 to-background-secondary dark:to-background-dark-tertiary'
                        : 'bg-background-tertiary dark:bg-background-dark-tertiary'
                    }`}
                  />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Progress Bar */}
      <div className="mt-4 max-w-4xl mx-auto">
        <div className="h-1 bg-background-tertiary dark:bg-background-dark-tertiary rounded-full overflow-hidden">
          <div
            className="h-full bg-primary-600 transition-all duration-300"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>
    </div>
  );
};

export default WizardStepIndicator;

